import constants from "../../../../../../appConstants";
import {
  SpecialNeedsRenderer,
  dispatchRoutesSpecialNeedsValueGetter,
  DotRenderer,
  totalUnAckPickupsValueGetter,
} from "../../../../../dataGrid/cellRenderer/index";
import { routeNameCellStyling } from "../../../../../dataGrid/cellStyling";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import {
  mergeUserConfig,
  formatDateTime,
  formatDateTimeWithoutUTCConversion,
} from "../../../../../dataGrid/gridUtilities";
import {
  MultiSelectFilter,
  DateTimeFilter,
  StaticFilter,
} from "../../../../../dataGrid/columnFilters";
import {
  LoggedInStatusIndicator,
  ClockedInStatusIndicator,
  ActionButtonRenderer,
  DriverNameCell,
  DriverStatusIcon,
  AccruedHoursCellRenderer,
  ElapseTimeRenderer,
  OccupiedCapacityRenderer,
  ETATimeRenderer,
} from "./cellRenderer";
import { DRIVER_LOGGED_STATUS } from "../../constants";
import {
  completedVsTotalStopsFormatter,
  getTotalInTrailer,
  getTotalReturnDeliveryShipments,
  getTotalDeliveryShipments,
  getPendingDeliveryShipments,
  getCompletedDeliveryShipments,
  getNoFreightpickupsCount,
  getTimeDifferenceInMinutes,
  getStopServiceWindowByStopType,
  getWeeklyAccruedHours,
  updateGridColumnHeaderNames,
} from "../../../utils/utils";
import CheckViolation from "../../../../../common/checkViolation";
import {
  hasStopDelayed,
  isDriverReadyForDispatch,
  renderTimeWithErrorIcon,
  getZoneValue,
  getArrivedAtOrEnrouteStop,
} from "../../utils";
import { getDriverStatus } from "../../utils/DriverStatus";
import { utcToZonedTime  } from "date-fns-tz";
import StyledToolTip from "../../../../../common/StyledToolTip";
import { gridShortHeaderNamesConfig } from "./constants";

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config);
  }
  static getConfiguration({ userProfile, featureFlags }) {
    const {
      enableColumnShortForm,
      enableDriverZone,
      enableWeeklyAccruedHours,
    } = featureFlags;
    const userConfig = userProfile.columnPref;
    const defaultConfig = GridConfig.getDefaultConfiguration({
      enableDriverZone,
      enableWeeklyAccruedHours,
    });
    const mergedConfig = mergeUserConfig({
      defaultConfig,
      userConfig,
    });
    return updateGridColumnHeaderNames({
      gridShortHeaderNamesConfig,
      columnConfig: mergedConfig,
      enableColumnShortForm,
    });
  }

  static getRouteCellClass(params) {
    return ["gridCell-leftAlign", "routeNameCell"];
  }
  static getUnReadMessageValue = (props) => {
    return props.data.unReadCount;
  };

  static totalPiecesValueGetter = (params) => {
    const { stops } = params.data;
    return getTotalInTrailer(stops, "pieces");
  };

  static totalReturnsValueGetter = (params) => {
    const { stops } = params.data;
    return getTotalReturnDeliveryShipments(stops);
  };

  static totalDeliveryShipments = (params) => {
    const { stops } = params.data;
    return getTotalDeliveryShipments(stops);
  };

  static totalPendingDeliveryShipments = (params) => {
    const { stops } = params.data;
    return getPendingDeliveryShipments(stops);
  };

  static totalCompletedDeliveryShipments = (params) => {
    const { stops } = params.data;
    return getCompletedDeliveryShipments(stops);
  };

  static totalNoFreightpickups = (params) => {
    const { stops } = params.data;
    return getNoFreightpickupsCount(stops);
  };

  static getValue = (params) => {
    return params.data[params.column.colId] || 0;
  };

  static renderCloseTime(props) {
    const selectedStop = getArrivedAtOrEnrouteStop(props);
    if (selectedStop) {
      const timeWindow = getStopServiceWindowByStopType(selectedStop);
      if (timeWindow) {
        const closeTime = timeWindow.split("-")[1];
        const hasDelayed = hasStopDelayed(selectedStop);

        return (
          <StyledToolTip title={hasDelayed ? "Arriving Late" : closeTime}>
            {renderTimeWithErrorIcon(closeTime, hasDelayed)}
          </StyledToolTip>
        );
      }
    }
    return null;
  }

  static renderElapseTime(props, terminalOffset) {
    const { lastActionTime } = props.data;
    const timeArr = lastActionTime?.split(":");

    if (!lastActionTime?.length) {
      return null;
    }

    const today = utcToZonedTime(new Date(), terminalOffset);
    const timeDiff = getTimeDifferenceInMinutes(
      new Date(0, 0, 0, timeArr[0], timeArr[1]),
      new Date(0, 0, 0, today.getHours(), today.getMinutes())
    );
    return timeDiff;
  }

  static getWeeklyAccruedHours(props) {
    const dailyAccruedHours = props.context.getDailyAccruedHours(
      props.data.driverId
    );
    return getWeeklyAccruedHours(dailyAccruedHours, props.data.accruedHours);
  }

  static messageCell(props) {
    if (!props.data?.routeId) {
      return <></>;
    }

    const hasUnreadMessageCount = props.data.unReadCount > 0;
    let outlinedStyle = hasUnreadMessageCount
      ? "dispatch-message dispatch-new-message"
      : "dispatch-message";
    return (
      <div className="dispaly-message">
        <MailOutlineIcon
          className={outlinedStyle}
          onClick={() =>
            props.api.runCommand("openMessage", { routeData: props.data })
          }
        />
        {hasUnreadMessageCount ? (
          <span className="dispatch-new-message-number">
            {props.data.unReadCount}
          </span>
        ) : null}
      </div>
    );
  }

  static stopsValueGetter(params) {
    return completedVsTotalStopsFormatter(params.data);
  }

  static DriverStatusRenderer(params) {
    const { driverClockedInTime, isCompanyDriver } = params.data;
    if (!isDriverReadyForDispatch(driverClockedInTime, isCompanyDriver)) {
      return <ClockedInStatusIndicator {...params} />;
    } else if (
      params?.data?.loggedInStatus === DRIVER_LOGGED_STATUS.loggedOut
    ) {
      return <LoggedInStatusIndicator {...params} />;
    }
    return <DotRenderer {...params} />;
  }

  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static getOccupiedCapacity(params) {
    const numberNullCheckArray = [null, undefined, ""];
    if (
      !numberNullCheckArray.includes(params.data.occupiedPse) &&
      !numberNullCheckArray.includes(params.data.trailer.maxSkid)
    ) {
      return `${params.data.occupiedPse}/${params.data.trailer.maxSkid}`;
    }
    return "";
  }

  static totalWeightValueGetter(params) {
    const { stops } = params.data;
    return getTotalInTrailer(stops, "weight");
  }

  static totalHUValueGetter(params) {
    const { stops } = params.data;
    return getTotalInTrailer(stops, "hu");
  }

  static getDefaultConfiguration({
    enableDriverZone,
    enableWeeklyAccruedHours,
  }) {
    const defaultConfig = {
      title: constants.gridNames.DispatchedDriverGrid,
      keyColumn: "routeId",
      rowSelection: "multiple",
      rowDeselection: true,
      rowClassRules: {
        violationRowBGColor: (params) =>
          Boolean(
            CheckViolation.checkDriverHasDelayStops({
              data: { stops: params.data.stops },
            })
          ),
      },
      defaultColDef: {
        lockPinned: true,
        sortable: true,
        filter: true,
        resizable: true,
      },
      columns: [
        {
          headerName: "",
          minWidth: constants.columnWidth.VIOLATION_DOT,
          maxWidth: constants.columnWidth.VIOLATION_DOT,
          field: "",
          colId: "driverLoginStatus",
          pinned: "left",
          lockPosition: true,
          cellClass: ["gridCell-centerAlign", "gridCell-dot"],
          filter: false,
          cellRendererFramework: GridConfig.DriverStatusRenderer,
          isInvisibleInShowHide: true,
          gridTitle: constants.gridNames.DispatchedDriverGrid,
        },
        {
          headerName: "Action",
          headerTooltip: "Assign/UnAssign Pickup",
          minWidth: constants.columnWidth.ASSIGN_ACTION,
          cellClass: "gridCell-leftAlign",
          colId: "actionCol",
          cellRendererFramework: ActionButtonRenderer,
          pinned: "left",
          lockPosition: true,
          filter: false,
          sortable: false,
        },
        {
          headerName: "Route",
          headerTooltip: "Route Name",
          field: "routeName",
          minWidth: constants.columnWidth.ROUTE_NAME,
          cellClass: GridConfig.getRouteCellClass,
          cellStyle: routeNameCellStyling,
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Driver Number",
          headerTooltip: "Driver Number",
          field: "driverNumber",
          minWidth: constants.columnWidth.PICKUP_NUMBER,
          cellClass: "gridCell-leftAlign",
          pinned: "left",
          lockPosition: true,
          filterFramework: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING,
          },
        },
        {
          headerName: "Zone",
          headerTooltip: "Zone",
          colId: "zone",
          valueGetter: getZoneValue,
          minWidth: constants.columnWidth.DISPATCH_ZONE,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Acc Hrs",
          headerTooltip: "Accrued Hours (Today)",
          colId: "accruedHours",
          field: "accruedHours",
          valueGetter: (props) =>
            props.context.getDailyAccruedHours(props.data.driverId),
          cellRendererFramework: AccruedHoursCellRenderer,
          minWidth: constants.columnWidth.ACC_HRS,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Weekly Acc Hrs",
          headerTooltip: "Weekly Accrued Hours",
          field: "weeklyAccruedHours",
          colId: "weeklyAccruedHours",
          valueGetter: GridConfig.getWeeklyAccruedHours,
          cellRendererFramework: AccruedHoursCellRenderer,
          minWidth: constants.columnWidth.WEEKLY_ACC_HRS,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          hide: true,
        },
        {
          headerName: "Driver",
          field: "driverName",
          cellRendererFramework: (props) => (
            <DriverNameCell
              {...props}
              terminalOffset={props.context.terminalOffset}
            />
          ),
          headerTooltip: "Driver Name",
          minWidth: constants.columnWidth.DISPATCH_DRIVER_NAME,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Message",
          cellRendererFramework: GridConfig.messageCell,
          headerTooltip: "Message",
          valueGetter: GridConfig.getUnReadMessageValue,
          minWidth: constants.columnWidth.DRIVER_INFO_SMALL,
          cellClass: "gridCell-leftAlign",
          filter: false,
          sortable: false,
          field: "msg",
        },
        {
          headerName: "Driver Status",
          field: "currentDriverStatus",
          cellRendererFramework: DriverStatusIcon,
          valueGetter: getDriverStatus,
          tooltipValueGetter: (params) => params.value,
          headerTooltip: "Current Driver Status",
          minWidth: constants.columnWidth.DISPATCH_DRIVER_STATUS,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Close Time",
          cellRendererFramework: GridConfig.renderCloseTime,
          headerTooltip: "Close Time",
          minWidth: constants.columnWidth.W_110,
          cellClass: "gridCell-leftAlign",
          colId: "closeTime",
        },
        {
          headerName: "ETA",
          cellRendererFramework: ETATimeRenderer,
          headerTooltip: "ETA",
          minWidth: constants.columnWidth.ETA_TIME,
          cellClass: "gridCell-leftAlign",
          colId: "eta",
        },
        {
          headerName: "Last Active Time",
          field: "lastActionTime",
          headerTooltip: "Last Active Time",
          minWidth: constants.columnWidth.LAST_ACTIVE_TIME,
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
          },
        },
        {
          headerName: "Elapsed Time",
          field: "elapsedTime",
          valueGetter: (props) =>
            GridConfig.renderElapseTime(props, props.context.terminalOffset),
          cellRendererFramework: ElapseTimeRenderer,
          headerTooltip: "Elapsed Time",
          minWidth: constants.columnWidth.ELAPSE_TIME,
          cellClass: "gridCell-leftAlign",
        },
        {
          headerName: "LCT",
          headerTooltip: "Last connected time",
          field: "lct",
          valueGetter:(params) => params.context.getLCT(params.data.driverId),
          minWidth: constants.columnWidth.W_85,
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
          },
        },
        {
          headerName: "Unoccupied Trailer Space",
          field: "TrailerCapacity",
          headerTooltip: "Unoccupied Trailer Space",
          minWidth: constants.columnWidth.UNOCC_TRAILER_SPACE,
          cellClass: "gridCell-leftAlign",
          hide: true,
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Equip Type",
          field: "trailerType",
          minWidth: constants.columnWidth.TRAILER_TYPE,
          headerTooltip: "Trailer Type",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Stops",
          minWidth: constants.columnWidth.W_90,
          headerTooltip: "Completed Stops / Total Stops",
          cellClass: "gridCell-leftAlign",
          valueGetter: GridConfig.stopsValueGetter,
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Deliveries",
          headerClass: "groupHeaderParent",
          minWidth: constants.columnWidth.SPECIAL_INSTRUCTION,
          headerTooltip: "Deliveries",
          marryChildren: true,
          children: [
            {
              headerClass: "groupHeaderChildren",
              headerName: "Complete",
              headerTooltip: "Completed Deliveries",
              valueGetter: GridConfig.totalCompletedDeliveryShipments,
              minWidth: constants.columnWidth.W_110,
              filterFramework: MultiSelectFilter,
              field: "compDel",
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "Pending",
              headerTooltip: "Pending Deliveries",
              valueGetter: GridConfig.totalPendingDeliveryShipments,
              minWidth: constants.columnWidth.W_100,
              filterFramework: MultiSelectFilter,
              field: "pendingDel",
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "Return",
              headerTooltip: "Return Deliveries",
              minWidth: constants.columnWidth.W_95,
              valueGetter: GridConfig.totalReturnsValueGetter,
              filterFramework: MultiSelectFilter,
              field: "returnDel",
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "Total",
              headerTooltip: "Total Deliveries",
              valueGetter: GridConfig.totalDeliveryShipments,
              minWidth: constants.columnWidth.W_85,
              filterFramework: MultiSelectFilter,
              field: "totalDel",
            },
          ],
        },
        {
          headerName: "Pickups",
          headerClass: "groupHeaderParent",
          marryChildren: true,
          children: [
            {
              headerClass: "groupHeaderChildren",
              headerName: "Complete",
              headerTooltip: "Completed Pickups",
              field: "completedPickups",
              valueGetter: GridConfig.getValue,
              minWidth: constants.columnWidth.W_110,
              filterFramework: MultiSelectFilter,
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "UnAck",
              headerTooltip: "Unacknowledged Pickups",
              field: "unAcknowledgedPickups",
              minWidth: constants.columnWidth.W_90,
              valueGetter: totalUnAckPickupsValueGetter,
              filterFramework: MultiSelectFilter,
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "Pending",
              headerTooltip: "Pending Pickups",
              field: "pendingPickups",
              valueGetter: GridConfig.getValue,
              minWidth: constants.columnWidth.W_100,
              filterFramework: MultiSelectFilter,
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "No Freight",
              headerTooltip: "No Freight Pickups",
              valueGetter: GridConfig.totalNoFreightpickups,
              minWidth: constants.columnWidth.W_115,
              filterFramework: MultiSelectFilter,
              field: "noFreightPickups",
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "Total",
              headerTooltip: "Total Pickups",
              field: "totalPickups",
              valueGetter: GridConfig.getValue,
              minWidth: constants.columnWidth.W_85,
              filterFramework: MultiSelectFilter,
            },
          ],
          minWidth: constants.columnWidth.SPECIAL_INSTRUCTION,
          headerTooltip: "Pickups",
          cellClass: "gridCell-centerAlign",
        },
        {
          headerName: "Spl Needs",
          minWidth: constants.columnWidth.SPL_NEED,
          valueGetter: dispatchRoutesSpecialNeedsValueGetter,
          headerTooltip: "Special Needs",
          cellClass: "gridCell-leftAlign",
          cellRendererFramework: SpecialNeedsRenderer,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption,
          },
          filterFramework: StaticFilter,
        },
        {
          headerName: "Gateout",
          field: "gateOutTime",
          minWidth: constants.columnWidth.W_95,
          headerTooltip: "Gateout Time",
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
          },
        },
        {
          headerName: "Proj MTY",
          field: "estimatedEmptyTime",
          minWidth: constants.columnWidth.W_100,
          headerTooltip: "Projected Empty Time",
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
          },
        },
        {
          headerName: "Total HU",
          minWidth: constants.columnWidth.TOTAL_HU,
          valueGetter: GridConfig.totalHUValueGetter,
          headerTooltip: "Total Handling Unit",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          field: "totalHU",
        },
        {
          headerName: "Total Weight",
          minWidth: constants.columnWidth.WEIGHT,
          headerTooltip: "Total Weight",
          valueGetter: GridConfig.totalWeightValueGetter,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          field: "totalWeight",
        },
        {
          headerName: "Total Pieces",
          minWidth: constants.columnWidth.TOTAL_PIECES,
          headerTooltip: "Total Pieces",
          valueGetter: GridConfig.totalPiecesValueGetter,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          field: "totalPcs",
        },
        {
          headerName: "Driver Type",
          minWidth: constants.columnWidth.DRIVER_TYPE,
          hide: true,
          headerTooltip: "Driver Type",
          field: "driverType",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Dispatched Time",
          minWidth: constants.columnWidth.DATE_TIME,
          headerTooltip: "Dispatched Time",
          field: "driverDispatchTime",
          valueFormatter: formatDateTimeWithoutUTCConversion,
          hide: true,
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME,
          },
        },
        {
          headerName: "ClockedIn Time",
          minWidth: constants.columnWidth.DATE_TIME,
          headerTooltip: "ClockedIn Time",
          valueFormatter: formatDateTime,
          hide: true,
          field: "driverClockedInTime",
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME,
          },
        },
        {
          headerName: "Occ.Capacity",
          minWidth: constants.columnWidth.OCC_PSE,
          headerTooltip: "Occupied Capacity",
          valueGetter: GridConfig.getOccupiedCapacity,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          field: "occCapacity",
          cellRendererFramework: OccupiedCapacityRenderer,
        },
      ],
    };
    const columns = defaultConfig.columns.filter(column => {
      switch (column.colId) {
        case "zone":
          return enableDriverZone;
        case "weeklyAccruedHours":
          return enableWeeklyAccruedHours;
        default:
          return true;
      }
    });
    return {...defaultConfig, columns};
  }
}
